import React, { useEffect, useRef, useState } from 'react';

/**
 * CountUp — animates a number from 0 to the target when scrolled into view.
 * Respects prefers-reduced-motion (renders the final value instantly).
 * Optional prefix ("£"), suffix ("M", "%"), and formatter callback.
 */
const CountUp = ({
  value,
  duration = 1400,
  prefix = '',
  suffix = '',
  format,
  className = '',
  testid,
}) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDisplay(value);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - t, 3);
              setDisplay(value * eased);
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.35 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [value, duration]);

  const rendered = format
    ? format(display)
    : Math.round(display).toLocaleString();

  return (
    <span ref={ref} className={className} data-testid={testid}>
      {prefix}{rendered}{suffix}
    </span>
  );
};

export default CountUp;
